import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ApplicationSettingsCard } from "@/components/forms/ApplicationSettingsCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FormRenderer } from "@/components/forms/FormRenderer";
import {
  ChevronLeft, Eye, Save, Rocket, Trash2, Copy, ArrowUp, ArrowDown, Lock, Plus,
  Type, AlignLeft, Mail, Phone, Hash, DollarSign, Calendar, Clock, ChevronDown as Cd,
  CircleDot, CheckSquare, ListChecks, ToggleRight, Link2, MapPin, Upload, EyeOff, Heading, Text,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  section: Heading, display: Text, short_text: Type, long_text: AlignLeft, email: Mail, phone: Phone,
  number: Hash, currency: DollarSign, date: Calendar, datetime: Clock, dropdown: Cd, radio: CircleDot,
  checkbox: CheckSquare, multi_select: ListChecks, yes_no: ToggleRight, url: Link2, address: MapPin,
  file_upload: Upload, hidden: EyeOff,
};
const CHOICE_TYPES = ["dropdown", "radio", "checkbox", "multi_select"];

export default function FormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);           // card meta incl. name/status/can_manage
  const [fields, setFields] = useState([]);
  const [settings, setSettings] = useState({});
  const [catalog, setCatalog] = useState(null);
  const [selId, setSelId] = useState(null);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [preview, setPreview] = useState(null);
  const [previewVals, setPreviewVals] = useState({});
  const dirtyRef = useRef(false);
  const timer = useRef(null);

  const load = async () => {
    try {
      const [{ data }, cat] = await Promise.all([api.get(`/forms/${id}`), api.get("/forms/field-catalog")]);
      setCatalog(cat.data);
      setMeta(data); setSettings(data.settings || {});
      // Viewing never creates a version: a draft is created only when the first change is saved.
      setFields((data.draft || data.published || { fields: [] }).fields);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const canManage = meta?.can_manage;
  const sel = fields.find((f) => f.id === selId);

  const saveRef = useRef(null);
  const markDirty = useCallback(() => {
    dirtyRef.current = true; setSaveState("dirty");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => saveRef.current && saveRef.current(true), 1400);
  }, []);

  const saveDraft = async (auto = false) => {
    if (!dirtyRef.current) return;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/forms/${id}/draft`, { fields, settings, meta: { name: meta.name, description: meta.description } });
      dirtyRef.current = false; setSaveState("saved");
      if (data.draft) setFields(data.draft.fields);
      if (!auto) toast.success("Draft saved");
    } catch (e) { setSaveState("dirty"); toast.error(apiError(e)); }
  };
  saveRef.current = saveDraft;

  const updateFields = (next) => { setFields(next); markDirty(); };

  const addField = (ft) => {
    const id2 = `new_${Math.random().toString(36).slice(2, 9)}`;
    const nf = { id: id2, field_type: ft, kind: ft === "section" ? "section" : (ft === "display" ? "display" : "response"),
      label: ft === "section" ? "New Section" : (ft === "display" ? "Add instructions here" : "New Question"),
      required: false, choices: CHOICE_TYPES.includes(ft) ? ["Option 1", "Option 2"] : [], width: "full", help: "", placeholder: "" };
    updateFields([...fields, nf]); setSelId(id2);
  };
  const addLibrary = (item) => {
    const id2 = `new_${Math.random().toString(36).slice(2, 9)}`;
    updateFields([...fields, { id: id2, field_type: item.field_type, kind: item.mapping ? "mapped" : "response",
      label: item.label, mapping: item.mapping || null, choices: item.choices || [], required: false, width: "full" }]);
    setSelId(id2);
  };
  const patchSel = (patch) => updateFields(fields.map((f) => f.id === selId ? { ...f, ...patch } : f));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= fields.length) return;
    const next = [...fields]; [next[i], next[j]] = [next[j], next[i]]; updateFields(next);
  };
  const dupField = (f) => {
    if (f.protected) { toast.error("Protected fields can't be duplicated"); return; }
    const id2 = `new_${Math.random().toString(36).slice(2, 9)}`;
    const idx = fields.findIndex((x) => x.id === f.id);
    const next = [...fields]; next.splice(idx + 1, 0, { ...f, id: id2, label: `${f.label} (copy)`, protected: false, mapping: null });
    updateFields(next); setSelId(id2);
  };
  const delField = (f) => {
    if (f.protected) { toast.error("Protected fields can't be removed"); return; }
    updateFields(fields.filter((x) => x.id !== f.id)); if (selId === f.id) setSelId(null);
  };

  const doPreview = () => {
    setPreviewVals({}); setPreview(fields);
  };
  const publish = async () => {
    await saveDraft(false);
    try {
      const v = await api.post(`/forms/${id}/validate`);
      if (!v.data.ok) { toast.error(v.data.errors[0] || "Fix validation errors first"); return; }
      await api.post(`/forms/${id}/publish`);
      toast.success("Form published"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!meta) return <div className="p-8 text-slate-400">Loading…</div>;

  const inputFields = fields.filter((f) => !["section", "display"].includes(f.field_type));

  return (
    <div className="space-y-4" data-testid="form-builder">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <button onClick={() => navigate("/admin/forms")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#006090]" data-testid="builder-back"><ChevronLeft className="w-4 h-4" />Forms</button>
          <div className="flex items-center gap-2 mt-1">
            <Input value={meta.name} onChange={(e) => { setMeta({ ...meta, name: e.target.value }); markDirty(); }} className="font-display font-bold text-lg border-0 px-0 h-auto focus-visible:ring-0 max-w-sm" data-testid="builder-name" disabled={!canManage} />
            <Badge variant="secondary" className="capitalize">{meta.scope_type}</Badge>
            <Badge className={meta.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{meta.status}</Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400" data-testid="builder-save-state">{saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved" : "Saved"}</span>
          <Button variant="outline" size="sm" onClick={doPreview} data-testid="builder-preview"><Eye className="w-4 h-4 mr-1.5" />Preview</Button>
          {canManage && <Button variant="outline" size="sm" onClick={() => saveDraft(false)} data-testid="builder-save"><Save className="w-4 h-4 mr-1.5" />Save Draft</Button>}
          {canManage && <Button size="sm" className="bg-[#006090] hover:bg-[#004d73] text-white" onClick={publish} data-testid="builder-publish"><Rocket className="w-4 h-4 mr-1.5" />Publish</Button>}
        </div>
      </div>

      {!canManage && <div className="text-sm bg-amber-50 text-amber-700 rounded-lg px-3 py-2">You have read-only access to this form's scope.</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[230px_minmax(0,1fr)_300px] gap-4">
        {/* LEFT — palette */}
        <div className="space-y-4" data-testid="builder-palette">
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Add Fields</p>
            <div className="grid grid-cols-2 gap-1.5">
              {(catalog?.field_types || []).map((t) => {
                const Ic = ICONS[t.type] || Type;
                return <button key={t.type} disabled={!canManage} onClick={() => addField(t.type)} data-testid={`palette-${t.type}`}
                  className="flex items-center gap-1.5 text-xs text-left px-2 py-1.5 rounded-md border border-slate-200 hover:border-[#006090] hover:bg-slate-50 disabled:opacity-40"><Ic className="w-3.5 h-3.5 text-slate-500 shrink-0" /><span className="truncate">{t.label}</span></button>;
              })}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Question Library</p>
            <div className="space-y-2 max-h-[320px] overflow-y-auto">
              {(catalog?.question_library || []).map((g) => (
                <div key={g.group}>
                  <p className="text-[10px] font-semibold text-slate-400 uppercase">{g.group}</p>
                  {g.items.map((it, i) => (
                    <button key={i} disabled={!canManage} onClick={() => addLibrary(it)} data-testid={`library-${g.group}-${i}`}
                      className="w-full flex items-center gap-1 text-xs text-left px-1.5 py-1 rounded hover:bg-slate-50 disabled:opacity-40"><Plus className="w-3 h-3 text-slate-400 shrink-0" /><span className="truncate">{it.label}</span></button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CENTER — canvas */}
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 min-h-[400px]" data-testid="builder-canvas">
          {fields.length === 0 && <div className="text-center text-slate-400 py-16 text-sm">Add fields from the left panel to build your form.</div>}
          <div className="space-y-2">
            {fields.map((f, i) => {
              const Ic = ICONS[f.field_type] || Type;
              const isSec = f.field_type === "section";
              return (
                <div key={f.id} onClick={() => setSelId(f.id)} data-testid={`canvas-field-${f.id}`}
                  className={`group rounded-lg border bg-white px-3 py-2.5 cursor-pointer transition-all ${selId === f.id ? "border-[#006090] ring-1 ring-[#006090]" : "border-slate-200 hover:border-slate-300"} ${isSec ? "bg-slate-100" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Ic className="w-4 h-4 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className={`truncate ${isSec ? "font-semibold text-slate-700" : "text-slate-800 text-sm"}`}>{f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}</p>
                      <p className="text-[11px] text-slate-400 truncate">{f.field_type.replace("_", " ")}{f.mapping ? ` · maps to ${f.mapping}` : ""}</p>
                    </div>
                    {f.protected && <Lock className="w-3.5 h-3.5 text-slate-400" title="Protected by Top-Recruit" />}
                    {canManage && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="p-1 hover:bg-slate-100 rounded" data-testid={`field-up-${f.id}`}><ArrowUp className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="p-1 hover:bg-slate-100 rounded" data-testid={`field-down-${f.id}`}><ArrowDown className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={(e) => { e.stopPropagation(); dupField(f); }} className="p-1 hover:bg-slate-100 rounded" data-testid={`field-dup-${f.id}`}><Copy className="w-3.5 h-3.5 text-slate-500" /></button>
                        <button onClick={(e) => { e.stopPropagation(); delField(f); }} className="p-1 hover:bg-rose-50 rounded" data-testid={`field-del-${f.id}`}><Trash2 className="w-3.5 h-3.5 text-rose-400" /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT — settings */}
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="builder-settings">
          {meta?.form_type === "candidate_application" && meta?.scope_type !== "system" && <div className="mb-3"><ApplicationSettingsCard form={meta} /></div>}
          <Tabs defaultValue="field">
            <TabsList className="w-full"><TabsTrigger value="field" className="flex-1">Field</TabsTrigger><TabsTrigger value="form" className="flex-1">Form</TabsTrigger></TabsList>
            <TabsContent value="field" className="mt-3 space-y-3">
              {!sel ? <p className="text-sm text-slate-400 py-6 text-center">Select a field to edit its settings.</p> : (
                <FieldSettings sel={sel} patch={patchSel} canManage={canManage} inputFields={inputFields} catalog={catalog} />
              )}
            </TabsContent>
            <TabsContent value="form" className="mt-3 space-y-3">
              <div><Label className="text-xs">Description</Label><Textarea value={meta.description || ""} onChange={(e) => { setMeta({ ...meta, description: e.target.value }); markDirty(); }} rows={2} disabled={!canManage} data-testid="form-setting-desc" /></div>
              <div><Label className="text-xs">Confirmation Message</Label><Textarea value={settings.confirmation_message || ""} onChange={(e) => { setSettings({ ...settings, confirmation_message: e.target.value }); markDirty(); }} rows={2} disabled={!canManage} data-testid="form-setting-confirm" /></div>
              <div><Label className="text-xs">Access</Label>
                <Select value={settings.access || "internal"} onValueChange={(v) => { setSettings({ ...settings, access: v }); markDirty(); }} disabled={!canManage}>
                  <SelectTrigger data-testid="form-setting-access"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="internal">Internal / Authenticated</SelectItem><SelectItem value="public">Public (requires review)</SelectItem></SelectContent></Select>
                <p className="text-[11px] text-slate-400 mt-1">Public forms need explicit security review before use.</p>
              </div>
              <div><Label className="text-xs">Effective Date</Label><Input type="date" value={settings.effective_date || ""} onChange={(e) => { setSettings({ ...settings, effective_date: e.target.value }); markDirty(); }} disabled={!canManage} data-testid="form-setting-effective" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Review Year</Label><Input type="number" value={settings.review_year || ""} onChange={(e) => { setSettings({ ...settings, review_year: Number(e.target.value) || null }); markDirty(); }} disabled={!canManage} data-testid="form-setting-review-year" /></div>
                <div><Label className="text-xs">Planning Year</Label><Input type="number" value={settings.planning_year || ""} onChange={(e) => { setSettings({ ...settings, planning_year: Number(e.target.value) || null }); markDirty(); }} disabled={!canManage} data-testid="form-setting-planning-year" /></div>
              </div>
              <div><Label className="text-xs">Reporting Cutoff Date</Label><Input type="date" value={settings.reporting_cutoff_date || ""} onChange={(e) => { setSettings({ ...settings, reporting_cutoff_date: e.target.value || null }); markDirty(); }} disabled={!canManage} data-testid="form-setting-cutoff" />
                <p className="text-[11px] text-slate-400 mt-1">Fills {"{review_year}"}, {"{planning_year}"} and {"{cutoff_date}"} in question text. Can be overridden per assignment.</p></div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="builder-preview-dialog">
          <DialogHeader><DialogTitle>{meta.name} — Preview</DialogTitle><DialogDescription className="sr-only">Live preview of the form as respondents will see it.</DialogDescription></DialogHeader>
          {preview && <FormRenderer fields={preview} values={previewVals} onChange={setPreviewVals} />}
          {settings.confirmation_message && <p className="text-xs text-slate-400 mt-3 border-t pt-2">On submit: {settings.confirmation_message}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FieldSettings({ sel, patch, canManage, inputFields, catalog }) {
  const isChoice = CHOICE_TYPES.includes(sel.field_type);
  const isLayout = ["section", "display", "question", "staff_review"].includes(sel.field_type);
  const isHeading = ["question", "staff_review"].includes(sel.field_type);
  const others = inputFields.filter((f) => f.id !== sel.id);
  const mapOptions = [...(catalog?.standard || []), ...(catalog?.custom || [])];
  const protectedKeys = new Set(catalog?.protected_keys || []);
  return (
    <>
      <div><Label className="text-xs">{sel.field_type === "display" ? "Text" : "Label"}</Label>
        <Input value={sel.label} onChange={(e) => patch({ label: e.target.value })} disabled={!canManage} data-testid="fs-label" /></div>
      <div><Label className="text-xs">Question Number</Label><Input value={sel.question_number || ""} onChange={(e) => patch({ question_number: e.target.value ? Number(e.target.value) || e.target.value : null })} disabled={!canManage} placeholder="e.g. 12 (shown as Q12.)" data-testid="fs-qnum" /></div>
      {isHeading && (
        <>
          <div><Label className="text-xs">Help Text</Label><Textarea rows={2} value={sel.help || ""} onChange={(e) => patch({ help: e.target.value })} disabled={!canManage} data-testid="fs-heading-help" /></div>
          <div><Label className="text-xs">Where to Find This Information</Label><Textarea rows={3} value={sel.where_to_find || ""} onChange={(e) => patch({ where_to_find: e.target.value })} disabled={!canManage} data-testid="fs-heading-where" /></div>
        </>
      )}
      {sel.field_type === "staff_review" && (
        <div><Label className="text-xs">Employee positions (one per line)</Label>
          <Textarea value={(sel.choices || []).join("\n")} onChange={(e) => patch({ choices: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={4} disabled={!canManage} data-testid="fs-staff-positions" /></div>
      )}
      {!isLayout && (
        <>
          <div><Label className="text-xs flex items-center gap-1">Question Code {sel.protected && <Lock className="w-3 h-3 text-slate-400" />}</Label>
            <Input value={sel.internal_name || ""} onChange={(e) => patch({ internal_name: e.target.value })} disabled={!canManage || sel.protected} className="font-mono text-xs" data-testid="fs-internal" />
            <p className="text-[11px] text-slate-400 mt-0.5">Permanent code used for reporting and year-over-year comparison. Keep it the same when the label changes.</p></div>
          <div><Label className="text-xs">Help Text</Label><Input value={sel.help || ""} onChange={(e) => patch({ help: e.target.value })} disabled={!canManage} data-testid="fs-help" /></div>
          <div><Label className="text-xs">Where to Find This Information</Label><Textarea rows={3} value={sel.where_to_find || ""} onChange={(e) => patch({ where_to_find: e.target.value })} disabled={!canManage} placeholder="Optional guidance shown in a collapsible help panel" data-testid="fs-where" /></div>
          {sel.field_type === "rating" && (
            <div className="grid grid-cols-2 gap-2" data-testid="fs-rating">
              <div><Label className="text-xs">Scale from</Label><Input type="number" value={sel.scale_min ?? 1} onChange={(e) => patch({ scale_min: Number(e.target.value) })} disabled={!canManage} data-testid="fs-scale-min" /></div>
              <div><Label className="text-xs">Scale to</Label><Input type="number" value={sel.scale_max ?? 5} onChange={(e) => patch({ scale_max: Number(e.target.value) })} disabled={!canManage} data-testid="fs-scale-max" /></div>
              <div><Label className="text-xs">Low label</Label><Input value={sel.scale_low_label || ""} onChange={(e) => patch({ scale_low_label: e.target.value })} disabled={!canManage} data-testid="fs-scale-low" /></div>
              <div><Label className="text-xs">High label</Label><Input value={sel.scale_high_label || ""} onChange={(e) => patch({ scale_high_label: e.target.value })} disabled={!canManage} data-testid="fs-scale-high" /></div>
            </div>
          )}
          <div><Label className="text-xs">Placeholder</Label><Input value={sel.placeholder || ""} onChange={(e) => patch({ placeholder: e.target.value })} disabled={!canManage} data-testid="fs-placeholder" /></div>
          <div className="flex items-center justify-between"><Label className="text-xs">Required {sel.protected && <span className="text-slate-400">(locked)</span>}</Label>
            <Switch checked={!!sel.required} onCheckedChange={(v) => patch({ required: v })} disabled={!canManage || sel.protected} data-testid="fs-required" /></div>
          <div><Label className="text-xs">Width</Label>
            <Select value={sel.width || "full"} onValueChange={(v) => patch({ width: v })} disabled={!canManage}><SelectTrigger data-testid="fs-width"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="full">Full</SelectItem><SelectItem value="half">Half</SelectItem></SelectContent></Select></div>
        </>
      )}
      {isChoice && (
        <div><Label className="text-xs">Choices (one per line)</Label>
          <Textarea value={(sel.choices || []).join("\n")} onChange={(e) => patch({ choices: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={4} disabled={!canManage} data-testid="fs-choices" /></div>
      )}
      {["checkbox", "multi_select"].includes(sel.field_type) && (
        <div className="grid grid-cols-1 gap-2">
          <div><Label className="text-xs">Maximum selections</Label><Input type="number" value={sel.max_select || ""} onChange={(e) => patch({ max_select: e.target.value ? Number(e.target.value) : null })} disabled={!canManage} placeholder="No limit" data-testid="fs-max-select" /></div>
          <div><Label className="text-xs">Exclusive choices (one per line)</Label>
            <Textarea rows={2} value={(sel.exclusive_choices || []).join("\n")} onChange={(e) => patch({ exclusive_choices: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} disabled={!canManage} placeholder="e.g. None of These" data-testid="fs-exclusive" />
            <p className="text-[11px] text-slate-400 mt-0.5">Selecting one of these clears every other choice.</p></div>
        </div>
      )}
      {!isLayout && (
        <div className="flex items-center justify-between"><Label className="text-xs">Confidential (hidden from exports and from viewers without Group Admin access)</Label>
          <Switch checked={!!sel.sensitive} onCheckedChange={(v) => patch({ sensitive: v })} disabled={!canManage} data-testid="fs-sensitive" /></div>
      )}
      {sel.prefill && (
        <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2" data-testid="fs-prefill">Prefilled from <span className="font-mono">{sel.prefill}</span>{sel.read_only ? " (read-only)" : " — changes are saved as proposed corrections, not applied to records"}.</p>
      )}
      {!isLayout && (
        <details className="border-t pt-2">
          <summary className="text-xs font-medium text-slate-500 cursor-pointer" data-testid="fs-advanced">Advanced — Data Mapping & Logic</summary>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs flex items-center gap-1">Maps to CRM Field {sel.protected && <Lock className="w-3 h-3 text-slate-400" />}</Label>
              {sel.protected ? <Input value={sel.mapping || ""} disabled className="font-mono text-xs" /> : (
                <Select value={sel.mapping || "__none"} onValueChange={(v) => patch({ mapping: v === "__none" ? null : v })} disabled={!canManage}>
                  <SelectTrigger data-testid="fs-mapping"><SelectValue placeholder="Response only" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    <SelectItem value="__none">Response only (not mapped)</SelectItem>
                    {mapOptions.filter((o) => !protectedKeys.has(o.key)).map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}
                  </SelectContent></Select>)}
              <p className="text-[11px] text-slate-400 mt-0.5">Mapped answers update the Person record; response-only answers stay with the submission.</p></div>
            <div><Label className="text-xs">Conditional Visibility</Label>
              <div className="grid grid-cols-1 gap-1.5">
                <Select value={sel.conditional?.field_id || "__none"} onValueChange={(v) => patch({ conditional: v === "__none" ? null : { field_id: v, operator: sel.conditional?.operator || "equals", value: sel.conditional?.value || "" } })} disabled={!canManage}>
                  <SelectTrigger data-testid="fs-cond-field"><SelectValue placeholder="Always show" /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="__none">Always show</SelectItem>{others.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent></Select>
                {sel.conditional?.field_id && (
                  <div className="flex gap-1.5">
                    <Select value={sel.conditional.operator} onValueChange={(v) => patch({ conditional: { ...sel.conditional, operator: v } })} disabled={!canManage}>
                      <SelectTrigger className="w-[120px]" data-testid="fs-cond-op"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="equals">equals</SelectItem><SelectItem value="not_equals">not equals</SelectItem>
                        <SelectItem value="in">is one of</SelectItem><SelectItem value="not_in">is none of</SelectItem>
                        <SelectItem value="contains">includes any of</SelectItem><SelectItem value="not_contains">does not include</SelectItem></SelectContent></Select>
                    <Input value={sel.conditional.value} onChange={(e) => patch({ conditional: { ...sel.conditional, value: e.target.value } })} placeholder="value (separate several with |)" disabled={!canManage} data-testid="fs-cond-val" />
                  </div>
                )}
              </div></div>
          </div>
        </details>
      )}
      {sel.protected && <p className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 flex items-start gap-1.5"><Lock className="w-3 h-3 mt-0.5 shrink-0" />Protected by Top-Recruit — identity/matching field. You can adjust its label but not remove or remap it.</p>}
    </>
  );
}
